/* sp-bundle.js — Theme v1 bundle contract on the v2 product section.
   Tier cards (`bundle_option` blocks), per-unit variant pickers for multi-variant products, add-to-cart
   (main lines → gift lines → discount code → cart drawer refresh) and the standard buy-button form signals
   third-party COD / buy-button apps read. No UI text lives here: strings come from data-t-* attributes
   rendered by Liquid (sp-t) on <sp-bundle-form> and on the bundle root [data-sp-bundle]. */
(function () {
  'use strict';

  var TEXT_DEFAULTS = {
    save: 'Save [amount]',
    off: '[pct]% OFF',
    unit: 'Item [n]',
    free: 'Free',
    gift: 'Gift',
    each: 'each',
    giftsIncluded: 'Included gifts',
    chooseOption: 'Choose an option',
    soldOut: 'Sold out',
    adding: 'Adding…',
    added: 'Added to cart',
    error: 'Could not add to cart. Please try again.'
  };
  var ADDED_FLASH_MS = 1600;

  function money(cents) {
    var n = Number(cents) || 0;
    if (window.sp && typeof window.sp.money === 'function') return window.sp.money(n);
    return (n / 100).toFixed(2);
  }

  // Sentinel placeholder fill: "[amount]" / "[pct]" / "[n]" (never innerHTML — values land in textContent).
  function fill(template, key, value) {
    return String(template == null ? '' : template).split('[' + key + ']').join(String(value));
  }

  function readJSON(id) {
    var el = document.getElementById(id);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent || 'null');
    } catch (e) {
      return null;
    }
  }

  // data-t-choose-option → text.chooseOption (only keys present and non-empty on the element).
  function readText(el) {
    var out = {};
    if (!el || !el.dataset) return out;
    Object.keys(el.dataset).forEach(function (k) {
      if (k.length < 2 || k.charAt(0) !== 't' || k.charAt(1) !== k.charAt(1).toUpperCase()) return;
      var key = k.charAt(1).toLowerCase() + k.slice(2);
      if (el.dataset[k]) out[key] = el.dataset[k];
    });
    return out;
  }

  // Standard buy-button contract: third-party buy-button / COD apps only see a variant selection through a
  // `change` event on the cart form's hidden [name=id] and the ?variant= URL param (many apps re-read it on
  // every history.replaceState). A bare `.value =` write is invisible to both. URL sync is skipped for the
  // homepage/featured context (updateUrl false).
  function syncFormSignals(form, variantId, updateUrl) {
    if (!variantId) return;
    var idInput = form ? form.querySelector('[name="id"]') : null;
    if (idInput) {
      idInput.value = String(variantId);
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (!updateUrl) return;
    try {
      var params = new URLSearchParams(window.location.search);
      params.set('variant', String(variantId));
      window.history.replaceState({}, '', window.location.pathname + '?' + params.toString() + window.location.hash);
    } catch (e) {
      // URL sync is best-effort — never break the PDP over it.
    }
  }

  // Availability is already part of sp-variant-map, rendered by Shopify Liquid.
  // Make sold-out pills visibly unavailable without depending on a second CSS upload.
  function installStockChoiceStyles() {
    if (document.getElementById('sp-bundle-stock-choice-styles')) return;
    var style = document.createElement('style');
    style.id = 'sp-bundle-stock-choice-styles';
    style.textContent =
      '.sp-bundle__color-option.is-unavailable{' +
      'opacity:.43!important;filter:grayscale(1);' +
      'cursor:not-allowed!important;text-decoration:line-through!important;' +
      'box-shadow:none!important;}' +
      '.sp-bundle__color-option.is-unavailable::after{' +
      'content:" (Tükendi)";font-size:10px;font-weight:500;text-decoration:none;}' +
      '.sp-bundle__color-option.is-unavailable:focus-visible{' +
      'outline:2px solid currentColor;outline-offset:2px;}';
    (document.head || document.documentElement).appendChild(style);
  }

  class SpBundleForm extends HTMLElement {
    connectedCallback() {
      if (this.spReady) return;
      this.sectionId = this.dataset.section;
      this.root = document.getElementById('MainProduct-' + this.sectionId) || this.closest('[data-section]') || document.body;
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.spReady = true;
      this.idInput = this.form.querySelector('[name="id"]');
      this.qtyInput = this.form.querySelector('[name="quantity"]');
      this.button = this.form.querySelector('[data-sp-atc]');
      this.buttonLabel = this.button ? this.button.querySelector('.sp-atc__label') : null;
      this.errorEl = this.querySelector('[data-sp-error]');
      this.bundleEl = this.root.querySelector('[data-sp-bundle]');
      this.sticky = this.root.querySelector('[data-sp-sticky-atc]');
      this.updateUrl = !(this.root.dataset && this.root.dataset.spContext === 'section');
      this.busy = false;
      this.unavailable = !!(this.button && this.button.hasAttribute('disabled'));

      this.config = readJSON('bundle-data-' + this.sectionId) || {};
      this.bundles = Array.isArray(this.config.bundles) ? this.config.bundles : [];
      this.variantMap = readJSON('sp-variant-map-' + this.sectionId);
      if (!Array.isArray(this.variantMap)) this.variantMap = [];
      this.optionNames = Array.isArray(this.config.optionNames) ? this.config.optionNames : [];
      var onlyDefault = this.config.hasOnlyDefaultVariant;
      if (typeof onlyDefault !== 'boolean') onlyDefault = this.variantMap.length <= 1;
      // Mixed-variant tiers (per-unit pickers) only exist on multi-variant products; single-variant products
      // keep the plain single line × quantity flow.
      this.hasVariants = !onlyDefault && this.variantMap.length > 0 && this.optionNames.length > 0;
      this.optionValues = this.hasVariants ? this.computeOptionValues() : [];
      if (this.hasVariants) installStockChoiceStyles();
      this.text = Object.assign({}, TEXT_DEFAULTS, readText(this), readText(this.bundleEl));

      this.cards = this.bundleEl ? Array.prototype.slice.call(this.bundleEl.querySelectorAll('[data-sp-tier]')) : [];
      this.bundleCount = this.cards.length;
      // Preserve each tier's individual color choices when the shopper changes tiers.
      this.unitChoices = Object.create(null);

      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.onSubmit();
      });
      this.cards.forEach((card) => {
        var input = card.querySelector('[data-sp-tier-input]');
        if (!input) return;
        input.addEventListener('change', () => this.selectTier(card));
        card.addEventListener('click', () => {
          if (!input.checked) {
            input.checked = true;
            this.selectTier(card);
          }
        });
      });

      // Variant picker hand-off: `sp:variant` on the section root ({ detail: { variant } }) re-bases the unit
      // price and re-syncs the hidden id. Dawn's pubsub `variantChange` is honoured too (section-filtered).
      this.onVariantEvent = (e) => {
        var v = e && e.detail && e.detail.variant;
        if (v && v.id) this.onVariant(v, e.detail && e.detail.source);
      };
      this.root.addEventListener('sp:variant', this.onVariantEvent);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS && PUB_SUB_EVENTS.variantChange) {
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (evt) => {
          var data = evt && evt.data;
          if (!data || !data.variant || !data.variant.id) return;
          if (data.sectionId && String(data.sectionId) !== String(this.sectionId)) return;
          this.onVariant(data.variant);
        });
      }
      // Theme editor: clicking a bundle block in the sidebar selects its tier.
      if (window.Shopify && window.Shopify.designMode) {
        this.onBlockSelect = (e) => {
          var card = e && e.target && typeof e.target.closest === 'function' ? e.target.closest('[data-sp-tier]') : null;
          if (!card || this.cards.indexOf(card) === -1) return;
          var input = card.querySelector('[data-sp-tier-input]');
          if (input) input.checked = true;
          this.selectTier(card);
        };
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      }

      this.renderCardPrices();
      var initial = this.checkedCard() || this.cards[0] || null;
      if (initial) {
        var initialInput = initial.querySelector('[data-sp-tier-input]');
        if (initialInput && !initialInput.checked) initialInput.checked = true;
        this.selectTier(initial);
      } else {
        this.updateSticky(null);
      }
    }

    disconnectedCallback() {
      if (this.root && this.onVariantEvent) this.root.removeEventListener('sp:variant', this.onVariantEvent);
      if (typeof this.unsubscribeVariant === 'function') this.unsubscribeVariant();
      if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    /* ---------- lookups ---------- */

    checkedCard() {
      for (var i = 0; i < this.cards.length; i++) {
        var input = this.cards[i].querySelector('[data-sp-tier-input]');
        if (input && input.checked) return this.cards[i];
      }
      return null;
    }

    tierIndex(card) {
      var idx = card ? parseInt(card.dataset.index, 10) : NaN;
      return isNaN(idx) ? -1 : idx;
    }

    bundleFor(card) {
      var idx = this.tierIndex(card);
      return idx >= 0 ? this.bundles[idx] || null : null;
    }

    currentVariantId() {
      var raw = this.idInput && this.idInput.value ? this.idInput.value : this.config.productVariantId;
      var id = Number(raw);
      return id && !isNaN(id) ? id : 0;
    }

    findVariant(id) {
      return (
        this.variantMap.find(function (v) {
          return v && Number(v.id) === Number(id);
        }) || null
      );
    }

    /* ---------- pricing ---------- */

    // Same math as Theme v1: tier total = unit × qty − (percent | fixed) discount; compare = the larger of the
    // retail compare-at total and the undiscounted total; a discount exists whenever compare > final.
    computePrice(bundle) {
      var unit = Number(this.config.productPrice) || 0;
      var retailUnit = Number(this.config.productComparePrice) || 0;
      var qty = bundle && bundle.mainQuantity > 0 ? Number(bundle.mainQuantity) : 1;
      var mainTotal = unit * qty;
      var discount = 0;
      if (bundle) {
        if (bundle.discountType === 'percent' && bundle.discountValue > 0) {
          discount = Math.round((mainTotal * bundle.discountValue) / 100);
        } else if (bundle.discountType === 'fixed' && bundle.discountValue > 0) {
          discount = Number(bundle.discountValue) * 100;
        }
      }
      var finalPrice = Math.max(0, mainTotal - discount);
      var compare = Math.max(retailUnit * qty, mainTotal);
      var hasDiscount = compare > finalPrice;
      return {
        qty: qty,
        finalPrice: finalPrice,
        compare: compare,
        hasDiscount: hasDiscount,
        saving: hasDiscount ? compare - finalPrice : 0,
        pct: hasDiscount && compare > 0 ? Math.round(((compare - finalPrice) / compare) * 100) : 0,
        perUnit: Math.round(finalPrice / qty)
      };
    }

    renderCardPrices() {
      this.cards.forEach((card) => {
        var bundle = this.bundleFor(card);
        if (!bundle) return;
        var p = this.computePrice(bundle);
        var priceEl = card.querySelector('[data-sp-tier-price]');
        if (priceEl) priceEl.textContent = money(p.finalPrice);
        var compareEl = card.querySelector('[data-sp-tier-compare]');
        if (compareEl) {
          compareEl.textContent = p.hasDiscount ? money(p.compare) : '';
          compareEl.hidden = !p.hasDiscount;
        }
        var saveEl = card.querySelector('[data-sp-tier-save]');
        if (saveEl) {
          saveEl.textContent = p.hasDiscount ? fill(this.text.save, 'amount', money(p.saving)) : '';
          saveEl.hidden = !p.hasDiscount;
        }
        var subtitleEl = card.querySelector('[data-sp-tier-subtitle][data-mode="each"]');
        if (subtitleEl) subtitleEl.textContent = money(p.perUnit) + ' ' + this.text.each;
      });
    }

    updateSticky(bundle) {
      if (!this.sticky) return;
      var p = this.computePrice(bundle);
      var priceEl = this.sticky.querySelector('[data-sp-sticky-price]');
      if (priceEl) priceEl.textContent = money(p.finalPrice);
      var compareEl = this.sticky.querySelector('[data-sp-sticky-compare]');
      if (compareEl) compareEl.textContent = p.hasDiscount ? money(p.compare) : '';
      var badge = this.sticky.querySelector('[data-sp-sticky-badge]');
      if (badge) {
        badge.hidden = !p.hasDiscount;
        var pctEl = badge.querySelector('[data-sp-sticky-percent]');
        if (pctEl) pctEl.textContent = String(p.pct);
      }
    }

    /* ---------- selection ---------- */

    selectTier(card) {
      var bundle = this.bundleFor(card);
      this.cards.forEach(function (c) {
        c.classList.toggle('is-selected', c === card);
      });
      this.current = card;
      this.updateSticky(bundle);
      this.updateCardVariants(card);
      // Buy-button contract: mirror the tier quantity into the hidden [name=quantity] (apps scan for it; a
      // programmatic write needs an explicit `change`) and re-sync the active main variant. Our own AJAX
      // submit never reads [name=quantity].
      if (this.qtyInput) {
        this.qtyInput.value = String(bundle && bundle.mainQuantity > 0 ? bundle.mainQuantity : 1);
        this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.syncPrimaryVariant();
    }

    onVariant(v, source) {
      // A separate product swatch must not force an unavailable color into the bundle.
      // Reassert the available bundle selection rather than selecting a sold-out unit.
      if (this.hasVariants && v.available === false && this.selectedUnits().length) {
        this.syncPrimaryVariant();
        return;
      }
      // The main product swatch changes unit 1 only; do not wipe unit 2's chosen color.
      // Other variant events keep the existing per-unit selectors unchanged.
      var fullVariant = this.findVariant(v.id);
      var nextOptions = Array.isArray(v.options) ? v.options : fullVariant && fullVariant.options;
      if (source === 'sp-variant-picker' && this.hasVariants && Array.isArray(nextOptions)) {
        var activeUnits = this.selectedUnits();
        if (activeUnits.length) {
          Array.prototype.forEach.call(activeUnits[0].querySelectorAll('select[data-opt]'), function (select) {
            var oi = Number(select.dataset.opt);
            if (oi >= 0 && oi < nextOptions.length) select.value = nextOptions[oi];
          });
          this.rememberUnitChoices(this.checkedCard());
        }
      }
      this.config.productVariantId = v.id;
      if (typeof v.price === 'number') this.config.productPrice = v.price;
      if (typeof v.compare_at_price === 'number' || v.compare_at_price === null) {
        this.config.productComparePrice = v.compare_at_price || 0;
      }
      syncFormSignals(this.form, v.id, this.updateUrl);
      if (typeof v.available === 'boolean') this.setAvailability(v.available);
      this.renderCardPrices();
      var card = this.checkedCard();
      if (card) this.selectTier(card);
      else this.updateSticky(null);
    }

    setAvailability(available) {
      this.unavailable = !available;
      var stickyButton = this.sticky ? this.sticky.querySelector('[data-sp-sticky-button]') : null;
      [this.button, stickyButton].forEach(function (btn) {
        if (!btn) return;
        if (available) btn.removeAttribute('disabled');
        else btn.setAttribute('disabled', '');
      });
      if (this.buttonLabel) {
        if (available) {
          if (this.button.dataset.spAtcLabel) this.buttonLabel.textContent = this.button.dataset.spAtcLabel;
        } else {
          this.buttonLabel.textContent = this.text.soldOut;
        }
      }
    }

    /* ---------- mixed-variant per-unit pickers ---------- */

    // Distinct values per option index, in first-seen (cartesian) order.
    computeOptionValues() {
      var out = this.optionNames.map(function () {
        return [];
      });
      this.variantMap.forEach(function (v) {
        (v && Array.isArray(v.options) ? v.options : []).forEach(function (val, i) {
          if (out[i] && out[i].indexOf(val) === -1) out[i].push(val);
        });
      });
      return out;
    }

    matchVariant(values) {
      return (
        this.variantMap.find(function (v) {
          return (
            v &&
            Array.isArray(v.options) &&
            v.options.length === values.length &&
            v.options.every(function (o, i) {
              return o === values[i];
            })
          );
        }) || null
      );
    }

    // A choice is enabled only if Shopify says a corresponding variant is available.
    // product.variants[].available also respects "Continue selling when out of stock".
    isOptionAvailable(optionIndex, value) {
      return this.variantMap.some(function (v) {
        return v && v.available !== false && Array.isArray(v.options) && v.options[optionIndex] === value;
      });
    }

    // Prefer an available variant even when the page was opened with a sold-out ?variant= ID.
    baseVariant() {
      var selected = this.findVariant(this.currentVariantId());
      if (selected && selected.available !== false) return selected;
      return this.variantMap.find(function (v) {
        return v && v.available !== false;
      }) || selected || this.variantMap[0] || null;
    }

    unitsHolder(card) {
      if (!card) return null;
      var holder = card.querySelector('[data-sp-units]');
      if (!holder) {
        // Some versions of sp-bundle-bar.liquid omit this holder after a tier block
        // is removed in the theme editor. Recreate it independently for each card.
        holder = document.createElement('span');
        holder.className = 'sp-bundle__units';
        holder.setAttribute('data-sp-units', '');
        holder.setAttribute('hidden', '');
        card.appendChild(holder);
      }
      return holder;
    }

    rememberUnitChoices(card) {
      if (!card) return;
      var holder = this.unitsHolder(card);
      var units = holder ? holder.querySelectorAll('.sp-bundle__unit') : [];
      if (!units.length) return;
      this.unitChoices[this.tierIndex(card)] = Array.prototype.map.call(units, function (unit) {
        return Array.prototype.map.call(unit.querySelectorAll('select[data-opt]'), function (select) {
          return select.value;
        });
      });
    }

    selectedUnits() {
      var holder = this.unitsHolder(this.checkedCard());
      return holder ? Array.prototype.slice.call(holder.querySelectorAll('.sp-bundle__unit')) : [];
    }

    // Render the per-unit pickers INTO the selected card only; empty + hide every other card's holder.
    updateCardVariants(selectedCard) {
      if (!this.hasVariants) return;
      this.cards.forEach((card) => {
        var holder = this.unitsHolder(card);
        if (!holder) return;
        if (card === selectedCard) {
          var bundle = this.bundleFor(card);
          var qty = bundle && bundle.mainQuantity > 0 ? Number(bundle.mainQuantity) : 1;
          if (holder.querySelectorAll('.sp-bundle__unit').length !== qty) {
            this.renderUnitPickersInto(holder, qty, this.unitChoices[this.tierIndex(card)]);
          }
          holder.removeAttribute('hidden');
        } else {
          this.rememberUnitChoices(card);
          holder.innerHTML = '';
          holder.setAttribute('hidden', '');
        }
      });
    }

    // Build a color-pill picker for each unit (one for 1-piece, two for 2-piece).
    // A hidden real <select> remains the source of truth for onSubmit(), snapshot() and COD bridge.
    // We use spans with role=radio rather than buttons because bundle cards can be <label> elements.
    renderUnitPickersInto(holder, qty, remembered) {
      var base = this.baseVariant();
      var defaults =
        base && Array.isArray(base.options)
          ? base.options
          : this.optionValues.map(function (vals) {
              return vals[0];
            });
      holder.innerHTML = '';
      for (var u = 0; u < qty; u++) {
        // A saved color must not remain selected after its stock reaches zero.
        var savedUnit = remembered && remembered[u] ? remembered[u] : null;
        var savedVariant = savedUnit ? this.matchVariant(savedUnit) : null;
        var unitDefaults = savedVariant && savedVariant.available !== false ? savedUnit : defaults;
        var unit = document.createElement('span');
        unit.className = 'sp-bundle__unit';
        unit.setAttribute('data-unit', String(u));
        // For other option types, keep the original per-unit heading.
        var hasColorOption = this.optionNames.some(function (n) {
          return /^(renk|color|colour)$/i.test(String(n).trim());
        });
        if (qty > 1 && !hasColorOption) {
          var label = document.createElement('span');
          label.className = 'sp-bundle__unit-label';
          label.textContent = fill(this.text.unit, 'n', u + 1);
          unit.appendChild(label);
        }
        for (var oi = 0; oi < this.optionNames.length; oi++) {
          var wrap = document.createElement('span');
          wrap.className = 'sp-bundle__unit-opt';
          var name = document.createElement('span');
          name.className = 'sp-bundle__unit-name';
          var isColor = /^(renk|color|colour)$/i.test(String(this.optionNames[oi]).trim());
          name.textContent = isColor && qty > 1 ? (u + 1) + '. ELDİVENİN RENGİ' : this.optionNames[oi];
          if (isColor) name.classList.add('sp-bundle__unit-name--color');
          wrap.appendChild(name);
          var select = document.createElement('select');
          select.className = 'sp-bundle__select' + (isColor ? ' sp-bundle__select--color-hidden' : '');
          select.setAttribute('data-opt', String(oi));
          if (isColor) {
            select.tabIndex = -1;
            select.setAttribute('aria-hidden', 'true');
          }
          select.setAttribute('aria-label', this.optionNames[oi]);
          // Any per-unit change re-syncs the active main variant (unit 1) so buy-button apps track it.
          select.addEventListener('change', () => this.syncPrimaryVariant());
          var vals = this.optionValues[oi] || [];
          for (var vi = 0; vi < vals.length; vi++) {
            var opt = document.createElement('option');
            opt.value = vals[vi];
            opt.textContent = vals[vi];
            var desired = unitDefaults[oi];
            // Keep the underlying select in sync with disabled color pills.
            if (!this.isOptionAvailable(oi, vals[vi])) opt.disabled = true;
            if (vals[vi] === desired) opt.selected = true;
            select.appendChild(opt);
          }
          wrap.appendChild(select);
          if (isColor) wrap.appendChild(this.createColorChoices(select, vals, name.textContent, oi));
          unit.appendChild(wrap);
        }
        holder.appendChild(unit);
      }
    }

    // Each group's events close over its OWN underlying select (no cross-unit color leaks).
    createColorChoices(select, vals, labelText, optionIndex) {
      var group = document.createElement('span');
      group.className = 'sp-bundle__color-choices';
      group.setAttribute('role', 'radiogroup');
      group.setAttribute('aria-label', labelText);
      var canChoose = (value) => this.isOptionAvailable(optionIndex, value);
      function refreshChoices() {
        Array.prototype.forEach.call(group.querySelectorAll('[data-sp-color-option]'), function (choice) {
          var chosen = choice.getAttribute('data-value') === select.value;
          var available = canChoose(choice.getAttribute('data-value'));
          choice.classList.toggle('is-selected', chosen && available);
          choice.classList.toggle('is-unavailable', !available);
          choice.setAttribute('aria-disabled', available ? 'false' : 'true');
          choice.setAttribute('aria-checked', chosen && available ? 'true' : 'false');
          choice.tabIndex = available && chosen ? 0 : -1;
        });
      }
      vals.forEach(function (value, vi) {
        var choice = document.createElement('span');
        choice.className = 'sp-bundle__color-option';
        choice.setAttribute('data-sp-color-option', '');
        choice.setAttribute('data-value', value);
        choice.setAttribute('data-color', String(value).toLocaleLowerCase('tr-TR'));
        choice.setAttribute('role', 'radio');
        choice.textContent = value;
        if (!canChoose(value)) {
          choice.classList.add('is-unavailable');
          choice.setAttribute('aria-disabled', 'true');
          choice.setAttribute('aria-label', value + ' — Tükendi');
          choice.setAttribute('title', 'Stokta yok');
        }
        function pick(event) {
          event.preventDefault();
          event.stopPropagation();
          if (!canChoose(value)) return;
          select.value = value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          refreshChoices();
        }
        choice.addEventListener('click', pick);
        choice.addEventListener('keydown', function (event) {
          if (!canChoose(value)) {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              event.stopPropagation();
            }
            return;
          }
          if (event.key === ' ' || event.key === 'Enter') return pick(event);
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown' || event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
            event.preventDefault();
            event.stopPropagation();
            var offset = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
            // Skip unavailable choices with keyboard navigation as well.
            for (var step = 1; step <= vals.length; step++) {
              var idx = (vi + offset * step + vals.length * (step + 1)) % vals.length;
              if (canChoose(vals[idx])) {
                var next = group.children[idx];
                if (next) { next.click(); next.focus(); }
                break;
              }
            }
          }
        });
        group.appendChild(choice);
      });
      select.addEventListener('change', refreshChoices);
      refreshChoices();
      return group;
    }

    // Resolve a unit element's selected option values → variant (or null).
    unitVariant(unitEl) {
      var selects = unitEl.querySelectorAll('select[data-opt]');
      return this.matchVariant(
        Array.prototype.map.call(selects, function (s) {
          return s.value;
        })
      );
    }

    // The "active main variant" a buy-button app should see is the selected card's unit-1 variant.
    syncPrimaryVariant() {
      if (!this.hasVariants) return;
      var units = this.selectedUnits();
      if (!units.length) return;
      this.rememberUnitChoices(this.checkedCard());
      var variants = units.map((unit) => this.unitVariant(unit));
      var allAvailable = variants.every(function (v) { return v && v.available !== false; });
      this.setAvailability(allAvailable);
      if (!allAvailable) return;
      var v = variants[0];
      if (this.currentVariantId() !== Number(v.id)) {
        this.config.productVariantId = v.id;
        this.config.productPrice = v.price;
        this.config.productComparePrice = v.compare_at_price || 0;
        this.renderCardPrices();
        this.updateSticky(this.bundleFor(this.checkedCard()));
      }
      syncFormSignals(this.form, v.id, this.updateUrl);
    }

    // Pure snapshot of the shopper's CURRENT selection, consumed by sp-cod-bridge.js. Returns null when there is
    // nothing meaningful to snapshot so the caller falls back to the standard form contract.
    getSelectionSnapshot() {
      var card = this.checkedCard();
      var bundle = this.bundleFor(card);
      if (!card || !bundle) return null;
      var primaryVariantId = this.currentVariantId();
      var mainQuantity = bundle.mainQuantity > 0 ? Number(bundle.mainQuantity) : 1;
      var units = [];
      if (this.hasVariants) {
        var unitEls = this.selectedUnits();
        if (unitEls.length === mainQuantity && unitEls.length > 0) {
          for (var i = 0; i < unitEls.length; i++) {
            var v = this.unitVariant(unitEls[i]);
            if (!v || v.available === false) return null;
            units.push({ variantId: v.id });
          }
        }
      }
      if (units.length === 0) {
        for (var k = 0; k < mainQuantity; k++) units.push({ variantId: primaryVariantId });
      }
      if (
        units.some(function (u) {
          return !u.variantId || isNaN(u.variantId);
        })
      ) {
        return null;
      }
      var distinct = {};
      units.forEach(function (u) {
        distinct[u.variantId] = true;
      });
      var gifts = (bundle.gifts || [])
        .filter(function (g) {
          return g && g.variantId;
        })
        .map(function (g) {
          return { variantId: g.variantId, quantity: g.quantity > 0 ? g.quantity : 1 };
        });
      return {
        tierIndex: this.tierIndex(card),
        mainQuantity: mainQuantity,
        units: units,
        gifts: gifts,
        discountCode: bundle.discountCode || '',
        mixed: Object.keys(distinct).length > 1,
        primaryVariantId: primaryVariantId
      };
    }

    /* ---------- add to cart ---------- */

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || '';
      this.errorEl.hidden = !message;
    }

    setLoading(on) {
      this.busy = !!on;
      if (!this.button) return;
      this.button.classList.toggle('is-loading', !!on);
      this.button.setAttribute('aria-busy', on ? 'true' : 'false');
      var spinner = this.button.querySelector('.sp-atc__spinner');
      if (spinner) spinner.hidden = !on;
      if (on) {
        this.button.setAttribute('disabled', '');
        this.button.setAttribute('aria-label', this.text.adding);
      } else {
        this.button.removeAttribute('aria-label');
        if (!this.unavailable) this.button.removeAttribute('disabled');
      }
    }

    flashAdded() {
      if (!this.button || !this.buttonLabel) return;
      var original = this.button.dataset.spAtcLabel || this.buttonLabel.textContent;
      this.buttonLabel.textContent = this.text.added;
      clearTimeout(this.addedTimer);
      this.addedTimer = setTimeout(() => {
        if (!this.unavailable) this.buttonLabel.textContent = original;
      }, ADDED_FLASH_MS);
    }

    onSubmit() {
      if (this.busy) return;
      this.showError('');
      var card = this.checkedCard();
      var bundle = this.bundleFor(card);
      // Classic single-product path: no selectable tier (every bundle_option block hidden or absent).
      if (!bundle) return this.onSubmitClassic();

      var qty = bundle.mainQuantity > 0 ? Number(bundle.mainQuantity) : 1;
      var variantId = this.currentVariantId();
      if (!variantId) return this.showError(this.text.chooseOption);

      var mainProps = { _bundle_label: bundle.label || '' };
      if (bundle.discountCode) mainProps._discount_code = bundle.discountCode;
      var items = [];

      // Mixed-variant tier: each unit becomes its own line (qty 1) with the shopper's per-unit variant.
      // Same-variant units merge server-side into one line × qty; the product-scoped min-qty automatic
      // discount evaluates on the aggregate either way. Otherwise the plain single line × qty.
      var units = this.hasVariants ? this.selectedUnits() : [];
      if (this.hasVariants && units.length === qty && units.length > 0) {
        for (var i = 0; i < units.length; i++) {
          var v = this.unitVariant(units[i]);
          if (!v) return this.showError(this.text.chooseOption);
          if (v.available === false) return this.showError(this.text.soldOut);
          items.push({ id: v.id, quantity: 1, properties: mainProps });
        }
      } else {
        var main = this.findVariant(variantId);
        if (main && main.available === false) return this.showError(this.text.soldOut);
        items.push({ id: variantId, quantity: qty, properties: mainProps });
      }

      (bundle.gifts || []).forEach(function (g) {
        if (!g || !g.variantId) return;
        items.push({
          id: g.variantId,
          quantity: g.quantity > 0 ? g.quantity : 1,
          properties: { _is_bundle_gift: 'true', _gift_label: g.label || '' }
        });
      });

      return this.addItems(items, bundle.discountCode || '');
    }

    onSubmitClassic() {
      var variantId = this.currentVariantId();
      if (!variantId) return this.showError(this.text.chooseOption);
      if (this.hasVariants) {
        var v = this.findVariant(variantId);
        if (!v) return this.showError(this.text.chooseOption);
        if (v.available === false) return this.showError(this.text.soldOut);
      }
      var parsed = this.qtyInput ? parseInt(this.qtyInput.value, 10) : 1;
      return this.addItems([{ id: variantId, quantity: parsed > 0 ? parsed : 1 }], '');
    }

    // Before sending a bundle, check the live Shopify cart for gift variants.
    // An already-present bundle gift must not be added again when a second tier is selected.
    // Keep the rest of the bundle item lines, their variants and their properties unchanged.
    skipDuplicateGifts(items, root) {
      var hasGifts = items.some(function (item) {
        return item.properties && item.properties._is_bundle_gift === 'true';
      });
      if (!hasGifts) return Promise.resolve(items);

      return fetch(root + 'cart.js', {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' }
      })
        .then(function (response) {
          if (!response.ok) throw new Error('Cart check failed');
          return response.json();
        })
        .then(function (cartData) {
          if (!cartData || !Array.isArray(cartData.items)) throw new Error('Invalid cart response');
          var seenGifts = new Set();
          cartData.items.forEach(function (item) {
            if (item.properties && item.properties._is_bundle_gift === 'true') {
              seenGifts.add(String(item.variant_id || item.id));
            }
          });
          return items.filter(function (item) {
            if (!item.properties || item.properties._is_bundle_gift !== 'true') return true;
            var giftVariantId = String(item.id);
            if (seenGifts.has(giftVariantId)) return false;
            seenGifts.add(giftVariantId);
            return true;
          });
        });
    }

    // Add product lines + ONLY gifts not already in the cart, then apply discount and refresh the drawer.
    addItems(items, discountCode) {
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      var cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      var sectionIds =
        cart && typeof cart.getSectionsToRender === 'function'
          ? cart.getSectionsToRender().map(function (s) {
              return s.id;
            })
          : null;
      // Lock the button BEFORE the preflight fetch so repeated clicks cannot start parallel requests.
      this.setLoading(true);
      return this.skipDuplicateGifts(items, root)
        .then(function (filteredItems) {
          var body = { items: filteredItems };
          // Without a discount code the drawer HTML can ride along the add call; with one it is fetched after the
          // code is applied so the drawer shows the discounted totals.
          if (sectionIds && !discountCode) {
            body.sections = sectionIds;
            body.sections_url = window.location.pathname;
          }
          return fetch(root + 'cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/javascript',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify(body)
          });
        })
        .then(function (res) {
          return res.json().then(function (data) {
            return { ok: res.ok, data: data };
          });
        })
        .then((result) => {
          var data = result.data || {};
          if (!result.ok || data.status) {
            var err = new Error('cart/add failed');
            err.userMessage = data.description || data.message || '';
            throw err;
          }
          if (!discountCode) return data;
          return this.applyDiscountCode(root, discountCode).then(function () {
            if (!sectionIds) return data;
            return fetch(root + 'cart?sections=' + sectionIds.join(','))
              .then(function (r) {
                return r.json();
              })
              .then(function (sections) {
                data.sections = sections;
                return data;
              })
              .catch(function () {
                return data;
              });
          });
        })
        .then((data) => {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate) {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: items[0].id, cartData: data });
          }
          this.flashAdded();
          this.refreshCart(cart, data, root);
        })
        .catch((err) => {
          this.showError(err && err.userMessage ? err.userMessage : this.text.error);
        })
        .then(() => {
          this.setLoading(false);
        });
    }

    // Applying the tier's Shopify discount code = riding the /discount/<code> URL, which sets the checkout
    // discount cookie. Best-effort: a failure keeps the (already correct) cart lines.
    applyDiscountCode(root, code) {
      var url = root + 'discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(root + 'cart');
      return fetch(url, { credentials: 'same-origin' })
        .then(function () {})
        .catch(function () {});
    }

    refreshCart(cart, data, root) {
      if (cart && cart.tagName === 'CART-DRAWER' && typeof cart.renderContents === 'function' && data && data.sections) {
        cart.classList.remove('is-empty');
        var inner = cart.querySelector('.drawer__inner');
        if (inner) inner.classList.remove('is-empty');
        cart.renderContents(data);
        return;
      }
      window.location.href = root + 'cart';
    }
  }

  if (!customElements.get('sp-bundle-form')) customElements.define('sp-bundle-form', SpBundleForm);
})();
